import React, { useState, useEffect } from "react";
import CloseIcon from "@mui/icons-material/Close";
import { Link } from "react-router-dom";
import InputAdornment from "@mui/material/InputAdornment";
import { Button, TextField, IconButton } from "@mui/material";
import { calculateTimeDifference, getTimeTrialStatus } from "../../utils";
import uploadIcon from "../../assets/upload_icon.svg";
import emptyStarIcon from "../../assets/empty_star.svg";
import goldenStarIcon from "../../assets/golden_star.svg";
import timerIcon from "../../assets/timetrails/timerIcon.svg";
import homeIcon from "../../assets/timetrails/home_icon.svg";
import iconRight from "../../assets/timetrails/icon_right.svg";
import "./TimeTrailsTask.css";
import { RequiredAsterisk } from "../UI/RequiredAsterisk/RequiredAsterisk";

export const TimeTrailsTaskDetails = ({
  currentTask,
  formData,
  urlError = "",
  onFileUpload,
  onUrlChange,
  onUrlUpload,
  onCommentsChange,
  onRemoveFile,
  onSubmit,
  onCancel,
  isSubmitting = false,
}) => {
  const stripHttpProtocol = (value = "") =>
    value.trim().replace(/^https?:\/\//i, "");

  const [isDragOver, setIsDragOver] = useState(false);
  const [countdown, setCountdown] = useState("");

  // Countdown timer effect
  useEffect(() => {
    if (!currentTask?.endDate) return;

    const updateCountdown = () => {
      const now = new Date();
      const endDate = new Date(currentTask.endDate);
      const diffInMs = endDate.getTime() - now.getTime();

      if (diffInMs <= 0) {
        setCountdown("Time's up!");
        return;
      }

      const diffInSeconds = Math.floor(diffInMs / 1000);
      const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
      const hours = Math.floor(diffInMinutes / 60);
      const minutes = diffInMinutes % 60;
      const seconds = diffInSeconds % 60;
      const days = Math.floor(hours / 24);
      const remainingHours = hours % 24;

      let result = "";

      if (days > 0) {
        result += `${days} day${days > 1 ? "s" : ""} `;
      }

      if (remainingHours > 0) {
        result += `${remainingHours}h:${minutes
          .toString()
          .padStart(2, "0")}m:${seconds.toString().padStart(2, "0")}s`;
      } else if (minutes > 0) {
        result += `${minutes}m:${seconds.toString().padStart(2, "0")}s`;
      } else if (seconds > 0) {
        result += `${seconds}s`;
      } else {
        result += "Less than 1s";
      }

      setCountdown(result.trim());
    };

    // Update immediately
    updateCountdown();

    // Update every second
    const interval = setInterval(updateCountdown, 1000);

    return () => clearInterval(interval);
  }, [currentTask?.endDate]);

  // Check if there's existing submission data
  const hasExistingSubmission = currentTask?.taskCompletion;

  // Check if task is still active
  const isTaskActive = () => {
    if (!currentTask?.endDate) return false;
    const now = new Date();
    const endDate = new Date(currentTask.endDate);
    return now < endDate;
  };

  // Check if task is expired
  const isTaskExpired = () => {
    const status = getTimeTrialStatus(currentTask);
    return status === "EXPIRED";
  };

  // Check if task should show golden star (approved, active, or pending)
  const shouldShowGoldenStar = () => {
    const status = getTimeTrialStatus(currentTask);
    return status === "APPROVED" || status === "PENDING" || isTaskActive();
  };

  // Drag and drop handlers
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      onFileUpload({
        target: {
          files: [files[0]],
        },
      });
    }
  };

  return (
    <div className="timetrails-task__main">
      {/* Breadcrumbs */}
      <div className="timetrails-task__breadcrumbs">
        <Link to="/quest" className="breadcrumb-link">
          <div className="breadcrumb-home-icon-container">
            <img src={homeIcon} alt="Home" className="breadcrumb-home-icon" />
          </div>
          <span className="breadcrumb-link-text">Home</span>
        </Link>
        <img
          src={iconRight}
          alt="icon_right"
          className="breadcrumb-home-icon-right"
        />

        <span className="breadcrumb-current">Time Trial Task</span>
      </div>

      {/* Task Details */}
      <div className="timetrails-task__details">
        <div className="timetrails-task__title">
          <h1 className="timetrails-task__title-text">
            Task:{" "}
            <span className="timetrails-task__title-text_span">
              {currentTask.task}
            </span>
          </h1>
          {countdown === "Time's up!" ? (
            <div className="expired-badge">
              <span>Expired</span>
            </div>
          ) : (
            <div className="timetrails-task__timer">
              <div className="timer-icon">
                <img src={timerIcon} alt="Timer" />
              </div>
              <h2 className="timer_countdown">
                {countdown ||
                  calculateTimeDifference(
                    currentTask.startDate,
                    currentTask.endDate,
                  )}
              </h2>
            </div>
          )}
        </div>

        {/* Show existing submission if available */}
        {hasExistingSubmission && (
          <>
            {/* Display submitted image if available */}
            {currentTask.taskCompletion.attachments?.filename && (
              <div className="timetrails-task__upload-section">
                <div className="uploaded-image-container">
                  <div className="uploaded-image">
                    <img
                      src={currentTask?.taskCompletion?.attachments?.filename}
                      alt="Submitted content"
                      className="uploaded-image-preview"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Display submitted URL */}
            {currentTask?.taskCompletion.attachments?.url && (
              <div className="timetrails-task__url-section">
                <label className="url-label">Add URL</label>
                <div className="url-input-container">
                  <TextField
                    fullWidth
                    placeholder="example.com/path"
                    value={
                      stripHttpProtocol(
                        currentTask?.taskCompletion.attachments?.url ||
                          formData?.url ||
                          "",
                      )
                    }
                    variant="outlined"
                    disabled
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">https://</InputAdornment>
                      ),
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "#f4f4f4 !important",
                        border: "none !important",
                        "& fieldset": {
                          border: "none !important",
                        },
                        "&:hover fieldset": {
                          border: "none !important",
                        },
                        "&.Mui-focused fieldset": {
                          border: "none !important",
                        },
                      },
                    }}
                  />
                </div>
              </div>
            )}

            {/* Display submitted comments if available */}
            <div className="timetrails-task__note-section">
              <label className="note-label">
                Additional comments to the submission
              </label>
              <TextField
                fullWidth
                multiline
                rows={3}
                placeholder="Comments..."
                value={
                  currentTask?.taskCompletion?.comments || formData?.comments
                }
                variant="outlined"
                disabled
                sx={{
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "#f4f4f4 !important",
                    border: "none !important",
                    "& fieldset": {
                      border: "none !important",
                    },
                    "&:hover fieldset": {
                      border: "none !important",
                    },
                    "&.Mui-focused fieldset": {
                      border: "none !important",
                    },
                  },
                }}
              />
            </div>

            {/* Points section - always show regardless of status */}
            <div className="timetrails-task__actions">
              <div className="timetrails-task__points">
                <img
                  src={shouldShowGoldenStar() ? goldenStarIcon : emptyStarIcon}
                  alt="Star"
                  className="star-icon"
                />

                <span className="points-text">
                  Points - {currentTask.points}
                </span>
              </div>
            </div>
          </>
        )}

        {/* Show editable form for new submission */}
        {!hasExistingSubmission && (
          <>
            {/* File Upload Section */}
            <div className="timetrails-task__upload-section">
              <label className="url-label">
                Upload image <RequiredAsterisk />
              </label>
              {!formData.selectedFile &&
              (!currentTask?.taskCompletion?.attachments?.filename ||
                formData.existingImageRemoved) ? (
                <div
                  className={`timetrails-task__upload-area ${
                    isDragOver ? "drag-over" : ""
                  } ${isTaskExpired() ? "disabled" : ""}`}
                  onDragOver={isTaskExpired() ? undefined : handleDragOver}
                  onDragLeave={isTaskExpired() ? undefined : handleDragLeave}
                  onDrop={isTaskExpired() ? undefined : handleDrop}
                >
                  <input
                    type="file"
                    id="file-upload"
                    onChange={onFileUpload}
                    accept="image/*"
                    style={{ display: "none" }}
                    disabled={isTaskExpired()}
                  />
                  <label htmlFor="file-upload" className="upload-label">
                    <img
                      src={uploadIcon}
                      alt="Upload"
                      className="upload-icon"
                    />
                    <div className="upload-text">
                      <p>
                        <span>Click to upload</span> or drag and drop here
                      </p>
                      <p className="upload-size">Maximum file size 1MB</p>
                      {isDragOver && (
                        <p className="drag-over-text">Drop your image here</p>
                      )}
                    </div>
                  </label>
                </div>
              ) : (
                <div className="uploaded-image-container">
                  <div className="uploaded-image-wrapper">
                    <img
                      src={
                        formData.selectedFile
                          ? URL.createObjectURL(formData.selectedFile)
                          : currentTask?.taskCompletion?.attachments?.filename
                      }
                      alt="Uploaded content"
                      className="uploaded-image-preview"
                    />
                    <IconButton
                      className="remove-image-btn"
                      onClick={onRemoveFile}
                      size="small"
                      disabled={isTaskExpired()}
                    >
                      <CloseIcon />
                    </IconButton>
                  </div>
                </div>
              )}
            </div>

            {/* URL Upload Section */}
            <div className="timetrails-task__url-section">
              <label className="url-label">
                Add URL <RequiredAsterisk />
              </label>
              <div className="url-input-container">
                <TextField
                  fullWidth
                  placeholder="example.com/path"
                  value={stripHttpProtocol(formData.url || "")}
                  onChange={onUrlChange}
                  variant="outlined"
                  error={Boolean(urlError)}
                  helperText={urlError}
                  disabled={isTaskExpired()}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">https://</InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "#f4f4f4 !important",
                      border: "none !important",
                      "& fieldset": {
                        border: "none !important",
                      },
                      "&:hover fieldset": {
                        border: "none !important",
                      },
                      "&.Mui-focused fieldset": {
                        border: "none !important",
                      },
                    },
                  }}
                />
                {/* <Button
                  variant="contained"
                  onClick={onUrlUpload}
                  className="upload-btn2"
                >
                  Upload
                </Button> */}
              </div>
            </div>

            {/* Additional Comments Section */}
            <div className="timetrails-task__note-section">
              <label className="note-label">
                Additional comments to the submission
              </label>
              <TextField
                fullWidth
                multiline
                rows={3}
                placeholder="Comments..."
                value={formData.comments}
                onChange={onCommentsChange}
                variant="outlined"
                disabled={isTaskExpired()}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "#f4f4f4 !important",
                    border: "none !important",
                    "& fieldset": {
                      border: "none !important",
                    },
                    "&:hover fieldset": {
                      border: "none !important",
                    },
                    "&.Mui-focused fieldset": {
                      border: "none !important",
                    },
                  },
                }}
              />
            </div>

            {/* Submission Actions */}
            <div className="timetrails-task__actions">
              <div className="timetrails-task__points">
                <img
                  src={shouldShowGoldenStar() ? goldenStarIcon : emptyStarIcon}
                  alt="Star"
                  className="star-icon"
                />

                <span className="points-text">
                  Points - {currentTask.points}
                </span>
              </div>
              {!isTaskExpired() && (
                <div className="timetrails-task__buttons">
                  <Button
                    variant="outlined"
                    onClick={onCancel}
                    className="cancel-btn"
                    sx={{
                      minWidth: "144px",
                      minHeight: "40px",
                      cursor: "pointer",
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    onClick={onSubmit}
                    className="send-btn"
                    disabled={
                      isSubmitting ||
                      (!formData.selectedFile && !formData.url.trim())
                    }
                    sx={{
                      minWidth: "144px",
                      minHeight: "40px",
                      cursor: "pointer",
                    }}
                  >
                    {isSubmitting ? "Sending..." : "Send"}
                  </Button>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};
